import { createSlice } from '@reduxjs/toolkit'
import axios from 'axios';
import { clearerror, setError } from './errorReducer'
import setAuthToken from '../utils/setAuthToken';
import jwt_decode from 'jwt-decode';
import isEmpty from '../validation/is-empty';
import { API_URL } from '../actions/constant';
import { getErrorData } from '../utils/getErrorData';

export const counterSlice = createSlice({
    name: 'auth',
    initialState: {
        isAuthenticated: false,
        user: {},
        userprofile: {},
        loading: false,
        registerDetail: {},
        verifyDetail: {},
        profileloading: false,
        // BUG-35 fix: distinguishes "we haven't successfully loaded a
        // profile yet because the last fetch errored" from "we loaded one
        // and it's genuinely empty/incomplete". Without this,
        // PrivateRoute's needsProfileCompletion() fallback heuristic
        // couldn't tell a network hiccup apart from a real new-user
        // profile, and redirected a perfectly complete account to
        // /user/complete-profile any time a getProfile() call failed.
        profileLoadFailed: false,
        // Set exactly once, at app bootstrap (App.js's initAuth effect),
        // after the JWT in localStorage has been restored (or found
        // absent/expired) and the resulting profile fetch — if any — has
        // resolved. This is what PrivateRoute/AdminRoute/FacilityManagerRoute
        // gate their initial "are we logged in yet" render on.
        //
        // Deliberately separate from `loading` / `profileloading` below.
        // Those two are toggled by a dozen unrelated thunks (attachEmail,
        // verifyAccount, attachWallet, loginUser, getProfile, ...) for their
        // OWN spinners — every one of those used to also flip the route
        // guards above off and on, unmounting whatever page the user was on
        // (e.g. mid-OTP-entry on Complete Your Profile) and remounting it
        // from scratch afterward, wiping local form state. authChecked never
        // gets touched again after bootstrap, so none of that can happen —
        // see setAuthChecked below.
        authChecked: false,
    },
    reducers: {
        currentUser: (state, action) => {
            state.user = action.payload
            state.isAuthenticated = !isEmpty(action.payload)
            state.loading = false
        },
        setLoading: (state, action) => {
            state.loading = action.payload
        },
        setAuthChecked: (state, action) => {
            state.authChecked = action.payload
        },
        registerUser: (state, action) => {
            state.registerDetail = action.payload
            state.loading = false
        },
        // Drops the pending-OTP / pending-confirmation state. registerDetail is
        // just "a login/register request is waiting on a code" — but it lives
        // in Redux, so unless something clears it, it outlives the OTP page
        // and every later visit to /login or /register sees it as a fresh,
        // in-progress attempt and redirects straight back to the code page
        // (the "Back button is stuck on the OTP page" bug).
        clearRegisterDetail: (state) => {
            state.registerDetail = {}
        },
        setProfile: (state, action) => {
            state.userprofile = action.payload
            // BUG-35 fix: a successful load clears any earlier failure so a
            // subsequent successful fetch is trusted again.
            state.profileLoadFailed = false
        },
        setProfileLoadFailed: (state, action) => {
            state.profileLoadFailed = action.payload
        },
        setProfileLoading: (state, action) => {
            state.profileloading = action.payload
        },
        clearUser: (state, action) => {
            state.isAuthenticated = false
            state.user = {}
            state.registerDetail = {}
            state.verifyDetail = {}

        },
        setLogout: (state, action) => {
            state.user = {}
            state.isAuthenticated = false
            state.registerDetail = {}
            state.verifyDetail = {}
            // BUG-36 fix: userprofile (and the load-state flags around it)
            // used to survive logout untouched, so the NEXT person to log
            // in on this browser/tab briefly saw the PREVIOUS user's cached
            // profile (name, KYC status, etc.) until getProfile() happened
            // to overwrite it — and any guard that reads userprofile before
            // that refetch completes was making decisions off stale,
            // wrong-user data.
            state.userprofile = {}
            state.profileloading = false
            state.profileLoadFailed = false
        },

    },
})

export const { currentUser, setLoading, setAuthChecked, setLogout, registerUser, clearRegisterDetail, clearUser, setCurrentUser, setProfile, setProfileLoading, setProfileLoadFailed } = counterSlice.actions

//API CALLS
//Login - Get User Token
export const loginUser = (userData) => dispatch => {
    dispatch(setLoading(true))
    dispatch(clearerror())
    axios.post('/api/user/login', userData)
        .then(res => {
            // Save to LocalStorage
            const { token } = res.data;
            if (token) {
                //Set Token to Localstorage
                localStorage.setItem('jwtToken', token);
                //Set Token to Header
                setAuthToken(token);
                //Decode Token to get user Data
                const decoded = jwt_decode(token);
                //set Current User
                dispatch(currentUser(decoded));
            } else {
                dispatch(registerUser(res.data));
            }

        })
        .catch(err => {

            dispatch(setError(getErrorData(err)))
            dispatch(setLoading(false))
        });
};

export const register = (userData) => dispatch => {
    dispatch(setLoading(true))
    axios.post('/api/user/register', userData)
        .then(res => {
            dispatch(registerUser(res.data));

        }).catch(err => {
            const errorData = getErrorData(err);
            dispatch(setError(errorData))
            dispatch(setLoading(false))
        });

}

// Adds email+password login to the CURRENTLY LOGGED IN account (the wallet
// -> "also add email login" flow) rather than creating a new account.
// Reuses the registerUser slice/registerDetail on success on purpose — it's
// the same shape ({ id }) that VerifyAccount.js already reads to drive the
// confirmation-code step, so that page and the /verify endpoint work
// unchanged for anything still using this via registerDetail. Also returns
// { success, error, id } directly so callers that want to stay in place
// (e.g. the inline wizard in EditProfile.js) don't have to wire up extra
// useEffects just to know whether the request worked.
export const attachEmail = (userData) => async dispatch => {
    dispatch(setLoading(true))
    dispatch(clearerror())
    try {
        const res = await axios.post('/api/user/attach-email', userData);
        dispatch(registerUser(res.data));
        dispatch(setLoading(false));
        return { success: true, id: res.data.id };
    } catch (err) {
        const errorData = getErrorData(err);
        dispatch(setError(errorData));
        dispatch(setLoading(false));
        return { success: false, error: errorData };
    }
}

// Returns { success, tokenIssued } so callers can act on the OUTCOME of
// this specific call instead of inferring it from state.auth.isAuthenticated
// after the fact. That distinction matters for the "attach email to an
// existing wallet account" flow: that account is ALREADY authenticated
// (attach-email requires being logged in) before the code is even sent, so
// isAuthenticated is true on page load regardless of whether THIS otp
// attempt succeeds — a caller that redirects on isAuthenticated alone (see
// VerifyAccount.js) would bounce away immediately, before the user can ever
// enter a code. tokenIssued reflects whether /verify actually returned a
// fresh token this call (it won't for an already-isVerified account that
// hits this by mistake, but the request still "succeeds").
export const verifyAccount = (userData) => async dispatch => {
    dispatch(setLoading(true))
    dispatch(setError({}))
    try {
        const res = await axios.post('/api/user/verify', userData);
        // Save to LocalStorage
        const { token } = res.data;
        if (token) {
            //Set Token to Localstorage
            localStorage.setItem('jwtToken', token);
            //Set Token to Header
            setAuthToken(token);
            //Decode Token to get user Data
            const decoded = jwt_decode(token);
            //set Current User
            dispatch(currentUser(decoded));
            // The JWT payload only carries id/email/firstName/lastName/
            // userType (see backend/utils/token.js) — mobile,
            // countryCode, walletAddress etc. live only in the full
            // profile, so without this the Complete Your Profile form
            // (EditProfile.js, reads state.auth.userprofile) renders
            // every field blank right after verifying, even the email
            // that was just submitted.
            dispatch(getProfile());
        }
        // Success never cleared the loading flag before — anything
        // showing a spinner off state.auth.loading (the /verifyaccount
        // page's own button, or the inline "Verify Email" flow on the
        // profile page) would spin forever after a correct code.
        dispatch(setLoading(false));
        return { success: true, tokenIssued: !!token };
    } catch (err) {
        dispatch(setError(getErrorData(err)))
        dispatch(setLoading(false))
        return { success: false, error: getErrorData(err) };
    }
}

export const verifyOTP = (userData) => dispatch => {
    dispatch(setLoading(true))
    dispatch(setError({}))
    axios.post('/api/user/verifyotp', userData)
        .then(res => {
            // Save to LocalStorage
            const { token } = res.data;
            if (token) {
                //Set Token to Localstorage
                localStorage.setItem('jwtToken', token);
                //Set Token to Header
                setAuthToken(token);
                //Decode Token to get user Data
                const decoded = jwt_decode(token);
                //set Current User
                dispatch(currentUser(decoded));
                // The OTP has been used — nothing is pending any more.
                dispatch(clearRegisterDetail());
                // NOTE: this used to call getProfile() without dispatching
                // it — getProfile() just returns a thunk function, it does
                // nothing unless dispatched, so the profile fetch never
                // actually ran. That's why userprofile.email showed up
                // empty on the Complete Your Profile page right after an
                // email+OTP login.
                dispatch(getProfile());
            }

        }).catch(err => {
            dispatch(setError(getErrorData(err)))
            dispatch(setLoading(false))
        });

}


export const loginUserViaWallet = (userData) => async (dispatch) => {
    dispatch(setLoading(true));
    dispatch(clearerror());

    try {
        const res = await axios.post('/api/user/walletlogin', userData);
        const { token } = res.data;

        if (token) {
            // Save to localStorage
            localStorage.setItem('jwtToken', token);

            // Set token to auth header
            setAuthToken(token);

            // Decode token to get user data
            const decoded = jwt_decode(token);

            // Set current user
            dispatch(currentUser(decoded));

            dispatch(setLoading(false));
            dispatch(getProfile())

            return { success: true, user: decoded };
        } else {
            dispatch(registerUser(res.data));
            dispatch(setLoading(false));

            return { success: false, needsRegistration: true };
        }
    } catch (err) {
        const errorData = getErrorData(err, 'Wallet login failed');
        dispatch(setError(errorData));
        dispatch(setLoading(false));

        return { success: false, error: errorData };
    }
}

// Adds a wallet to the CURRENTLY LOGGED IN account (the email-login ->
// "also connect a wallet" flow — mirror of attachEmail above). Unlike
// loginUserViaWallet, this does NOT log the user into whichever account
// (if any) owns that wallet — it attaches the wallet to the account
// that's already signed in. Backend (POST /api/user/wallet) enforces one
// wallet per account and rejects a wallet already tied to someone else.
//
// Requires `signature` — a signature over the wallet-nonce challenge (see
// signWalletLoginChallenge in utils/walletAuth.js), exactly like
// loginUserViaWallet needs for /walletlogin. POST /api/user/wallet proves
// ownership the same way /walletlogin does; without a signature it always
// 400s with "signature is required" (F-1 in SECURITY_AUDIT_REPORT.md is
// what added that requirement server-side). Callers must run the wallet
// through signWalletLoginChallenge(signer, walletAddress) first and pass
// the result in here.
export const attachWallet = (walletAddress, signature) => async (dispatch) => {
    dispatch(setLoading(true));
    dispatch(clearerror());
    try {
        const res = await axios.post('/api/user/wallet', { walletAddress, signature });
        // Refresh userprofile so EditProfile.js immediately shows the
        // newly connected wallet without needing a page reload.
        dispatch(getProfile());
        dispatch(setLoading(false));
        return { success: true, walletAddress: res.data.walletAddress };
    } catch (err) {
        const errorData = getErrorData(err, 'Failed to connect wallet');
        dispatch(setError(errorData));
        dispatch(setLoading(false));
        return { success: false, error: errorData };
    }
};

export const getProfile = () => async dispatch => {
    dispatch(setProfileLoading(true))
    dispatch(clearerror())
    try {
        let result = await axios.get(API_URL + "/api/user")
        if (result.data) {
            dispatch(setProfile(result.data))
            dispatch(setProfileLoading(false))
        }
    } catch (err) {
        dispatch(setProfileLoading(false))
        // BUG-35 fix: this used to leave userprofile exactly as it was
        // (usually still {}) with no record that the fetch failed, so
        // PrivateRoute's completeness check couldn't tell "profile hasn't
        // loaded yet / failed to load" from "profile loaded and is
        // genuinely empty" — a transient network error looked identical to
        // a brand-new, incomplete account.
        dispatch(setProfileLoadFailed(true))
    }
}

export default counterSlice.reducer